import { useCallback, useEffect, useRef, useState } from 'react';
import { API, authFetch, getAuth } from '../api';

/**
 * Loads the Graphs page data for a given range.
 *
 * Two return signals serve different parts of the screen:
 *   - bannerStatus drives the top notice ("Start streaming…" /
 *     "Wait 12 hours…"). Always present, drives the empty / partial
 *     state copy.
 *   - results is the chart data. Empty array is valid (case 1 / 2 —
 *     "no results" placeholder); a populated array means we have
 *     past measurements to render even if a current one is mid-flight.
 *
 * range — 'latest' | '1w' | '1m' | '1y'. Anything outside this set
 *         is forwarded as-is so backend additions don't need a
 *         frontend redeploy to be requestable.
 */
export function useGraphOutput(range) {
  const [bannerStatus, setBannerStatus] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bumped by refresh() to re-fire the effect on demand.
  const [tick, setTick] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const auth = getAuth();
    if (!auth) {
      setResults([]);
      setBannerStatus(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await authFetch(API.modelGraphOutput(range), { method: 'GET' });

        if (cancelled || !mountedRef.current) return;

        if (!res.ok) {
          setError(`Failed to load graph data (${res.status})`);
          setLoading(false);
          return;
        }

        const json = await res.json();
        if (cancelled || !mountedRef.current) return;

        setBannerStatus(json.bannerStatus);
        setResults(Array.isArray(json.latestResults) ? json.latestResults : []);
        setLoading(false);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Network error');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [range, tick]);

  return { bannerStatus, results, loading, error, refresh };
}
